"use client";

import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    createColumnHelper,
    ExpandedState,
    getCoreRowModel,
    getExpandedRowModel,
    getSortedRowModel,
    Row,
    useReactTable
} from "@tanstack/react-table";
import {AutoEqu, AutoTableInterface, columns as baseColumns, FlatItem} from "@/features/types/AutoTableInterface";
import {ReactTable, ReactTableState} from "@/shared/components/ReactTable";
import {Button} from "@/shared/components/ui/button";
import {ChevronRight, FileText, SquarePen, X} from "lucide-react";
import AutoTableHeaderFilter from "@/features/components/AutoTable/AutoTableHeaderFilter/AutoTableHeaderFilter";
import {ColumnVisibilityToggler} from "@/shared/components/ColumnVisibilityToggler/ColumnVisibilityToggler";
import {TableRow} from "@/shared/components/ui/table";
import {Virtualizer} from "@tanstack/react-virtual";
import {ReactTableCell} from "@/shared/components/ReactTable/ReactTableCell/ReactTableCell";
import {FilterToolbox} from "@/shared/components/FilterToolbox/FilterToolbox";
import {TooltipProvider} from "@/shared/components/ui/tooltip";
import {Checkbox} from "@/shared/components/ui/checkbox";
import {cn} from "@/shared/lib/utils";
import {Input} from "@/shared/components/ui/input";
import AutoTableRowDetails from "@/features/components/AutoTable/AutoTableRowDetails/AutoTableRowDetails";
import {getRowColor, rowItemStyles} from "@/features/components/AutoTable/AutoTable.styles";
import FilterPresetButton from "@/shared/components/FilterPresetButton/FilterPresetButton";
import {useTablePreset} from "@/shared/hooks/useTablePreset";
import {StatusChangeModal} from "../StatusChangeModal";
import {AutoDetailsModal} from "@/features/components/AutoDetailsModal";
import {Toast} from "@/shared/components/Toast";
import {
    changeDatabase,
    checkSession,
    getAutoEquTableDetails,
    getAutoTableData,
    getAutoTableRowById,
    getUserName
} from "@/features/controllers/AutoTableController";
import {changeStatusActionLabels, StatusCode} from "@/features/types/StatusChangeInterface";
import {FilterKey, nullableOptions} from "@/features/types/AutoTableFilterInterface";
import {AlertPopup} from "@/shared/components/AlertPopup";
import {useUser} from "@/shared/lib/UserContext";
import {getActionsForStatus, getAlertProps, validateStatusChange} from "@/features/services/StatusChangeService";
import {fetchDealerApplicationsList} from "@/shared/lib/dealerAuth";

const columnHelper = createColumnHelper<AutoTableInterface>();

export function AutoTable() {
    const [activeFilterBox, setActiveFilterBox] = useState<{ columnId: FilterKey, x: number, y: number } | null>(null);
    const [statusModal, setStatusModal] = useState<React.ReactNode | null>(null);
    const [detailsRow, setDetailsRow] = useState<AutoTableInterface | null>(null);
    const [carData, setCarData] = useState<AutoTableInterface[]>([]);
    const [rowLimit, setRowLimit] = useState<number | undefined>(20000);
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [expandedData, setExpandedData] = useState<Record<string, AutoEqu[]>>({});
    const [tableState, setTableState] = useState<ReactTableState>("loading");
    const [containerWidth, setContainerWidth] = useState(0);
    const outerContainerRef = useRef<HTMLDivElement | null>(null);
    const tableContainerRef = useRef<HTMLDivElement | null>(null);
    const virtualizerRef = useRef<Virtualizer<HTMLDivElement, HTMLElement> | null>(null);

    const [statusToast, setStatusToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
    const [statusToastVisible, setStatusToastVisible] = useState(false);
    const statusToastTimeout = useRef<NodeJS.Timeout | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger do odświeżania danych
    const [userMismatchAlert, setUserMismatchAlert] = useState<{ rowStatus: string, rowUserName: string } | null>(null);
    const [stock2ReservationAlert, setStock2ReservationAlert] = useState<boolean>(false);
    const [statusActionDropdown, setStatusActionDropdown] = useState<{
        rowId: string,
        x: number,
        y: number,
        actions: StatusCode[]
    } | null>(null);
    const {userName: userNameFromContext, isManager} = useUser();
    const [userName, setUserName] = useState<string | null>(userNameFromContext || null);
    const [currentDatabase, setCurrentDatabase] = useState<string>("ACLUB_MO");
    const [isChangingDatabase, setIsChangingDatabase] = useState(false);
    const [pendingScrollId, setPendingScrollId] = useState<string | null>(null);
    const [highlightId, setHighlightId] = useState<string | null>(null);
    const [forcedRow, setForcedRow] = useState<AutoTableInterface | null>(null);
    const [isFetchingForcedRow, setIsFetchingForcedRow] = useState(false);
    const refreshTriggerWhenScrollSetRef = useRef<number>(-1);
    const scrollIdSetOnConfirmRef = useRef<boolean>(false);
    const prevTableRowsRef = useRef<Row<AutoTableInterface>[]>([]);
    const forcedRowScrollRef = useRef<string | null>(null);
    const tablePreset = useTablePreset();
    const {
        preset,
        setFilters,
        setSorting,
        columnVisibility,
        columnOrder,
        columnSizing,
        presetsLoaded,
    } = tablePreset;

    const showStatusToast = useCallback((msg: string, type: "success" | "error" = "success") => {
        setStatusToast({message: msg, type});
        setStatusToastVisible(true);

        if (statusToastTimeout.current) clearTimeout(statusToastTimeout.current);

        statusToastTimeout.current = setTimeout(() => {
            setStatusToastVisible(false);
            setTimeout(() => setStatusToast(null), 300);
        }, 2500);
    }, []);

    const fetchAndForceRow = useCallback(async (rowId: string) => {
        if (!rowId || isFetchingForcedRow) return;
        if (forcedRow && String(forcedRow.f_id) === rowId) return;

        setIsFetchingForcedRow(true);
        try {
            const row = await getAutoTableRowById(rowId);
            if (row) {
                setForcedRow(row);
                forcedRowScrollRef.current = null;
                showStatusToast("Uwaga: wiersz nie pasuje do aktywnych filtrów - pokazuję go mimo to.", "error");
            } else {
                showStatusToast("Nie udało się odnaleźć wiersza po zapisie.", "error");
            }
        } catch (err) {
            console.error("Błąd podczas pobierania wiersza po ID:", err);
            showStatusToast("Nie udało się pobrać wiersza po zapisie.", "error");
        } finally {
            setIsFetchingForcedRow(false);
        }
    }, [forcedRow, isFetchingForcedRow, showStatusToast]);

    // Pobierz userName jeśli nie jest dostępny z kontekstu
    useEffect(() => {
        if (!userName && !userNameFromContext) {
            getUserName()
                .then(setUserName)
                .catch((err) => {
                    console.error("Błąd podczas pobierania userName:", err);
                });
        }
    }, [userName, userNameFromContext]);

    // Aktualizuj userName gdy zmienia się w kontekście
    useEffect(() => {
        if (userNameFromContext) {
            setUserName(userNameFromContext);
        }
    }, [userNameFromContext]);

    // Pobierz aktualną bazę danych przy starcie
    useEffect(() => {
        checkSession().then((session) => {
            if (session.defaultFilter) {
                setCurrentDatabase(session.defaultFilter);
            }
        }).catch(console.error);
    }, []);

    // Smoke-test nowej autoryzacji Dealer API (tylko DEV): pobranie listy aplikacji.
    useEffect(() => {
        if (process.env.NODE_ENV !== "development") return;

        const runDealerAuthSmokeTest = async () => {
            try {
                const apps = await fetchDealerApplicationsList();
                console.log("[Dealer API] applications/list:", apps);
            } catch (err) {
                console.error("[Dealer API] applications/list error:", err);
            }
        };

        void runDealerAuthSmokeTest();
    }, []);

    // Funkcja do zmiany bazy danych
    const handleDatabaseChange = async (newDatabase: string) => {
        if (newDatabase === currentDatabase || isChangingDatabase) return;

        setIsChangingDatabase(true);
        try {
            await changeDatabase(newDatabase);
            setCurrentDatabase(newDatabase);
            // Odśwież dane po zmianie bazy
            setRefreshTrigger(prev => prev + 1);
            showStatusToast(`Baza danych zmieniona na: ${newDatabase}`);
        } catch (error: unknown) {
            console.error("Błąd podczas zmiany bazy danych:", error);
            const errorMessage = error instanceof Error ? error.message : "Nieznany błąd";
            showStatusToast(`Błąd podczas zmiany bazy: ${errorMessage}`);
        } finally {
            setIsChangingDatabase(false);
        }
    };

    useEffect(() => {
        if (!presetsLoaded) return;

        const delayDebounce = setTimeout(() => {
            setTableState("loading");

            getAutoTableData(preset.filters, rowLimit)
                .then((data: AutoTableInterface[]) => {
                    setCarData(data);
                    setTableState(data.length === 0 ? "empty" : "ready");
                })
                .catch((err: unknown) => {
                    console.error("Błąd ładowania tabeli:", err);
                    setCarData([]);
                    setTableState("error");
                });
        }, 100);
        return () => clearTimeout(delayDebounce);
    }, [preset.filters, rowLimit, presetsLoaded, refreshTrigger]);

    useEffect(() => {
        if (!forcedRow) return;
        const existsInData = carData.some((row) => String(row.f_id) === String(forcedRow.f_id));
        if (existsInData) {
            setForcedRow(null);
        }
    }, [carData, forcedRow]);

    useEffect(() => {
        const updateWidth = () => {
            if (outerContainerRef.current) {
                setContainerWidth(outerContainerRef.current.offsetWidth);
            }
        };

        window.addEventListener("resize", updateWidth);
        updateWidth();
        return () => window.removeEventListener("resize", updateWidth);
    }, []);

    // Zamknij dropdown gdy klikniesz poza nim
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (statusActionDropdown) {
                const target = e.target as HTMLElement;
                // Sprawdź czy kliknięcie było poza dropdownem (nie jest przyciskiem akcji ani dropdownem)
                const isActionButton = target.closest('button[aria-label="Zmień status"]');
                const isDropdown = target.closest('.fixed.z-\\[60\\]');
                if (!isActionButton && !isDropdown) {
                    setStatusActionDropdown(null);
                }
            }
        };
        if (statusActionDropdown) {
            // Użyj setTimeout aby uniknąć natychmiastowego zamknięcia
            setTimeout(() => {
                document.addEventListener("mousedown", handleClickOutside);
            }, 0);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }
    }, [statusActionDropdown]);

    const handleExpandRow = useCallback((row: Row<AutoTableInterface>) => {
        const rowId = row.id;
        // Używamy expanded state bezpośrednio zamiast row.getIsExpanded()
        // aby uniknąć problemów z synchronizacją po sortowaniu
        const expandedState = typeof expanded === 'object' ? expanded : {};
        const isAlreadyOpen = expandedState[rowId];

        if (isAlreadyOpen) {
            setExpanded({});
            setExpandedData({});
        } else {
            setExpandedData({});
            setExpanded({[rowId]: true});
        }
    }, [expanded]);

    const handleFilterSort = (columnId: FilterKey, e: React.MouseEvent) => {
        e.preventDefault();
        setActiveFilterBox(null);

        if (e.button === 2) {
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            setActiveFilterBox({
                columnId,
                x: rect.left + rect.width / 2,
                y: rect.bottom - 4,
            });
        }
    };

    const openStatusModal = useCallback(async (row: Row<AutoTableInterface>, targetStatusCode: StatusCode) => {
        // Sprawdź czy akcja jest obsługiwana (tylko "4", "5", "" są zaimplementowane)
        const isActionSupported = targetStatusCode === "4" || targetStatusCode === "5" || targetStatusCode === "";
        if (!isActionSupported) {
            const status = row.original.f_supp_2_status_desc || "";
            const statusLabel = status || "pusty";
            showStatusToast(`Dla statusu "${statusLabel}" nie ma obecnie dostępnej akcji`, "error");
            return;
        }

        // Walidacja przez Service (sprawdza stock2, userMismatch itp.)
        const validation = await validateStatusChange({
            row,
            targetStatusCode,
            currentUserName: userName,
            onUserNameFetch: setUserName,
        });

        if (!validation.canProceed && validation.alertType) {
            if (validation.alertType === "stock2") {
                setStock2ReservationAlert(true);
            } else if (validation.alertType === "userMismatch") {
                setUserMismatchAlert({
                    rowStatus: validation.alertData?.rowStatus || "",
                    rowUserName: validation.alertData?.rowUserName || "",
                });
            }
            return;
        }

        // Zapamiętaj f_id PRZED otwarciem modalu, aby móc scrollować do tego wiersza po zapisaniu
        const rowAutoId = String(row.original.f_id);
        setPendingScrollId(rowAutoId);
        scrollIdSetOnConfirmRef.current = false; // Reset flagi - będzie ustawiona na true tylko w onConfirm
        refreshTriggerWhenScrollSetRef.current = -1; // Reset ref - ważne! Zapobiega scrollowaniu przed onConfirm

        setStatusModal(
            <StatusChangeModal
                row={row}
                targetStatusCode={targetStatusCode}
                onConfirm={(result) => {
                    console.log('AutoTable onConfirm wywołane:', {targetStatusCode, result, pendingScrollId});
                    const actionLabel = changeStatusActionLabels[targetStatusCode] || "nieznana";
                    let message = `Pomyślnie zmieniono status na ${actionLabel}!`;

                    // Dodaj informacje z OUTPUT execa tylko dla zamówień
                    // Używamy discriminatora 'type' do sprawdzenia typu odpowiedzi
                    if (result && result.type === 'order') {
                        const details: string[] = [];
                        if (result.ord_num) {
                            details.push(`Nr zamówienia: ${result.ord_num}`);
                        }
                        if (result.ord_user_name) {
                            details.push(`Użytkownik: ${result.ord_user_name}`);
                        }
                        if (result.ord_year) {
                            details.push(`Rok: ${result.ord_year}`);
                        }
                        if (details.length > 0) {
                            message += ` (${details.join(', ')})`;
                        }
                    }
                    // Dla 'reservation' i 'cancel' nie dodajemy dodatkowych informacji

                    showStatusToast(message);

                    // Oznacz że pendingScrollId został ustawiony w onConfirm (akcja się powiodła)
                    scrollIdSetOnConfirmRef.current = true;

                    // Zapamiętaj aktualny refreshTrigger przed jego zwiększeniem
                    // (używamy funkcji aktualizującej, aby uzyskać aktualną wartość)
                    setRefreshTrigger(prev => {
                        refreshTriggerWhenScrollSetRef.current = prev;
                        console.log('AutoTable: Ustawiam refreshTriggerWhenScrollSetRef:', prev, 'nowy refreshTrigger:', prev + 1);
                        return prev + 1;
                    });
                }}
                onClose={() => {
                    console.log('AutoTable onClose wywołane:', {
                        targetStatusCode,
                        scrollIdSetOnConfirmRef: scrollIdSetOnConfirmRef.current,
                        refreshTriggerWhenScrollSetRef: refreshTriggerWhenScrollSetRef.current,
                        pendingScrollId
                    });
                    // Jeśli modal został zamknięty bez pomyślnego zapisania:
                    // - scrollIdSetOnConfirmRef.current === false (onConfirm nie został wywołany)
                    // - refreshTriggerWhenScrollSetRef.current === -1 (refreshTrigger nie został ustawiony w onConfirm)
                    // Wtedy wyczyść pendingScrollId
                    const wasConfirmed = scrollIdSetOnConfirmRef.current || refreshTriggerWhenScrollSetRef.current >= 0;
                    if (!wasConfirmed && pendingScrollId) {
                        console.log('AutoTable onClose: Czyszczę pendingScrollId bo akcja się nie powiodła');
                        setPendingScrollId(null);
                        refreshTriggerWhenScrollSetRef.current = -1; // Reset ref
                    } else {
                        console.log('AutoTable onClose: NIE czyszczę pendingScrollId bo akcja się powiodła lub pendingScrollId jest null');
                    }
                    setStatusModal(null);
                }}
            />
        );
    }, [pendingScrollId, showStatusToast, userName]);

    const handleStatusActionClick = useCallback(async (row: Row<AutoTableInterface>, e?: React.MouseEvent) => {
        const status = row.original.f_supp_2_status_desc || "";
        const statusCode = status ? status.split("-")[0].trim() : "";
        const availableActions = getActionsForStatus(statusCode, row, isManager, userName);

        // Jeśli nie ma dostępnych akcji, pokaż toast z informacją
        if (availableActions.length === 0) {
            const statusLabel = status || "-";
            showStatusToast(`Dla statusu "${statusLabel}" nie ma obecnie dostępnej akcji`, "error");
            return;
        }

        // Jeśli jest tylko jedna akcja, otwórz modal bezpośrednio (z walidacją)
        if (availableActions.length === 1) {
            await openStatusModal(row, availableActions[0]);
            return;
        }

        // Jeśli jest więcej akcji, pokaż dropdown
        // Walidacja stock2 będzie wykonana dopiero gdy użytkownik wybierze akcję "5" (Rezerwacja) z dropdowna
        if (e) {
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            setStatusActionDropdown({
                rowId: row.id,
                x: rect.left,
                y: rect.bottom + 4,
                actions: availableActions,
            });
        }
    }, [isManager, openStatusModal, showStatusToast, userName]);

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: "expand",
                size: 60,
                header: "",
                cell: ({row}) => {
                    const expandedState = typeof expanded === 'object' ? expanded : {};
                    const isExpanded = expandedState[row.id];
                    return (
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleExpandRow(row)}
                            style={{
                                cursor: "pointer",
                                transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                                transition: "transform 0.15s",
                                border: "none",
                                background: "transparent",
                            }}
                            aria-label={isExpanded ? "Zwiń wiersz" : "Rozwiń wiersz"}
                        >
                            <ChevronRight size={18}/>
                        </Button>
                    );
                },
            }),

            columnHelper.display({
                id: "details",
                header: "",
                cell: ({row}) => (
                    <Button
                        size="sm"
                        variant="ghost"
                        style={{
                            cursor: "pointer",
                            border: "none",
                            background: "transparent",
                        }}
                        onClick={() => setDetailsRow(row.original)}
                    >
                        <FileText size={18}/>
                    </Button>
                ),
                size: 60,
            }),

            columnHelper.display({
                id: "actions",
                header: "",
                cell: ({row}) => {
                    // Sprawdź czy akcja jest obsługiwana dla tego statusu (z uwzględnieniem uprawnień)
                    const status = row.original.f_supp_2_status_desc || "";
                    const statusCode = status ? status.split("-")[0].trim() : "";
                    const availableActions = getActionsForStatus(statusCode, row, isManager, userName);
                    const isActionEnabled = availableActions.length > 0;

                    return (
                        <Button
                            size="sm"
                            variant="ghost"
                            style={{
                                cursor: isActionEnabled ? "pointer" : "not-allowed",
                                border: "none",
                                background: "transparent",
                                opacity: isActionEnabled ? 1 : 0.4,
                            }}
                            aria-label="Zmień status"
                            onClick={(e) => handleStatusActionClick(row, e)}
                            disabled={!isActionEnabled}
                        >
                            <SquarePen size={18}/>
                        </Button>
                    );
                },
                size: 60,
            }),

            ...baseColumns.map((col) =>
                columnHelper.accessor(col.accessorKey, {
                    header: (info) => (
                        <AutoTableHeaderFilter
                            info={info}
                            name={col.header}
                            isFilterable={!!col.type}
                            onHeaderClick={handleFilterSort}
                        />
                    ),
                    meta: {
                        key: col.accessorKey,
                        label: col.header,
                        type: col.type,
                        isNumeric: ["numberInput", "date", "boolean"].includes(String(col.type)),
                    },
                    size: col.size,
                    cell: (info) => {
                        const value = info.getValue();
                        if (col.type === "boolean") {
                            const numericValue = Number(value);
                            const isChecked = numericValue === 1;
                            return (
                                <Checkbox
                                    checked={isChecked}
                                    onCheckedChange={() => {
                                    }}
                                    className={cn(isChecked ? "opacity-70" : "opacity-0")}
                                />
                            );
                        }
                        return value;
                    },
                })
            ),
        ],
        [expanded, handleExpandRow, handleStatusActionClick, isManager, userName]
    );

    const tableData = useMemo(() => {
        if (!forcedRow) return Array.isArray(carData) ? carData : [];
        const existsInData = carData.some((row) => String(row.f_id) === String(forcedRow.f_id));
        if (existsInData) return carData;
        return [forcedRow, ...carData];
    }, [carData, forcedRow]);

    const table = useReactTable<AutoTableInterface>({
        data: tableData,
        columns,
        getRowId: (row) => {
            return String(row.f_id);
        },
        columnResizeMode: "onChange",
        columnResizeDirection: "ltr",
        state: {
            columnVisibility: columnVisibility,
            columnOrder: columnOrder,
            columnSizing: columnSizing,
            sorting: preset.sorting,
            expanded,
        },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        onColumnVisibilityChange: tablePreset.handleColumnVisibilityChange,
        onColumnOrderChange: tablePreset.handleColumnOrderChange,
        onColumnSizingChange: tablePreset.handleColumnSizingChange,
        onSortingChange: setSorting,
        onExpandedChange: setExpanded,
    });
    const tableRows = table.getRowModel().rows;

    const flatRows = React.useMemo<FlatItem[]>(() => {
        const rows = tableRows;
        const out: FlatItem[] = [];
        const expandedState = typeof expanded === 'object' ? expanded : {};
        for (const r of rows) {
            out.push({type: "row", row: r});
            // Używamy expanded state bezpośrednio, aby zachować rozwinięcie po sortowaniu
            const rowId = r.id;
            if (expandedState[rowId]) {
                out.push({type: "detail", row: r});
            }
        }
        return out;
    }, [tableRows, expanded]);

    const estimateSize = useCallback((index: number) => {
        const item = flatRows[index];
        return item?.type === "row" ? 40 : 240;
    }, [flatRows]);

    useEffect(() => {
        virtualizerRef.current?.measure();
    }, [expanded, flatRows]);

    useEffect(() => {
        const expandedRowId = Object.keys(expanded)[0];
        if (!expandedRowId) return;

        const rowModel = table.getRowModel();
        const foundRow = rowModel.rows.find(r => r.id === expandedRowId);

        if (foundRow) {
            const {f_ord_num, f_ord_d} = foundRow.original;
            let isCancelled = false;

            (async () => {
                try {
                    const res = await getAutoEquTableDetails(f_ord_num, f_ord_d);
                    if (!isCancelled) {
                        setExpandedData({[expandedRowId]: res});
                    }
                } catch (err) {
                    console.error(err);
                }
            })();
            return () => {
                isCancelled = true;
            };
        }
    }, [expanded, table]);

    // Scrollowanie do wiersza po pomyślnej akcji zmiany statusu
    useEffect(() => {
        // Warunki wstępne
        if (!pendingScrollId) return;

        // Sprawdź czy tableRows się zmieniły (nowa referencja = nowa tablica po requery)
        const tableRowsChanged = prevTableRowsRef.current !== tableRows;

        // Sprawdź czy refreshTrigger się zwiększył (requery zostało rozpoczęte)
        const refreshTriggerWasSet = refreshTriggerWhenScrollSetRef.current >= 0;
        const refreshTriggerIncreased = refreshTriggerWasSet &&
            refreshTrigger > refreshTriggerWhenScrollSetRef.current;

        console.log('Scroll useEffect check:', {
            pendingScrollId,
            tableRowsChanged,
            refreshTriggerWasSet,
            refreshTriggerIncreased,
            refreshTrigger,
            refreshTriggerWhenScrollSetRef: refreshTriggerWhenScrollSetRef.current,
            tableRowsLength: tableRows.length,
            prevTableRowsLength: prevTableRowsRef.current.length
        });

        // Aktualizuj ref z tableRows (zawsze, aby mieć aktualną wartość do następnego porównania)
        // Ale sprawdzamy tableRowsChanged tylko gdy refreshTrigger się zwiększył
        prevTableRowsRef.current = tableRows;

        // Scrollowanie następuje tylko gdy:
        // 1. refreshTrigger został ustawiony w onConfirm (akcja się powiodła)
        // 2. refreshTrigger się zwiększył (requery zostało rozpoczęte)
        // 3. tableRows się zmieniły PO zwiększeniu refreshTrigger (dane się zaktualizowały)
        const shouldScroll = refreshTriggerWasSet && refreshTriggerIncreased && tableRowsChanged;

        if (!shouldScroll) {
            console.log('Scroll: Warunki nie spełnione', {
                tableRowsChanged,
                refreshTriggerWasSet,
                refreshTriggerIncreased,
                shouldScroll
            });
            return;
        }

        console.log('Scroll: Wszystkie warunki spełnione, szukam wiersza...');

        // Znajdź index wiersza w flatRows (tylko wiersze typu "row", nie "detail")
        const index = flatRows.findIndex(
            (item) => item.type === "row" && String(item.row.original.f_id) === pendingScrollId
        );

        if (index === -1) {
            console.log('Scroll: Index nie znaleziony w flatRows', pendingScrollId);
            void fetchAndForceRow(pendingScrollId);
            setPendingScrollId(null);
            scrollIdSetOnConfirmRef.current = false;
            refreshTriggerWhenScrollSetRef.current = -1;
            return;
        }

        console.log('Scroll: Przeskakuję do wiersza', pendingScrollId, 'index:', index);

        virtualizerRef.current?.scrollToIndex(index, {align: 'start', behavior: 'auto'});

        setHighlightId(pendingScrollId);
        setPendingScrollId(null);
        scrollIdSetOnConfirmRef.current = false;
        refreshTriggerWhenScrollSetRef.current = -1;
    }, [pendingScrollId, tableRows, flatRows, refreshTrigger, fetchAndForceRow]);

    useEffect(() => {
        if (!forcedRow) return;
        const forcedRowId = String(forcedRow.f_id);
        const index = flatRows.findIndex(
            (item) => item.type === "row" && String(item.row.original.f_id) === forcedRowId
        );
        if (index === -1) return;
        if (forcedRowScrollRef.current === forcedRowId) return;

        virtualizerRef.current?.scrollToIndex(index, {align: 'start', behavior: 'auto'});
        setHighlightId(forcedRowId);
        forcedRowScrollRef.current = forcedRowId;
    }, [forcedRow, flatRows]);

    // Automatyczne usunięcie highlight po 3 sekundach
    useEffect(() => {
        if (!highlightId) return;

        const timeout = setTimeout(() => {
            setHighlightId(null);
        }, 3000);

        return () => clearTimeout(timeout);
    }, [highlightId]);

    const totalTableWidth = Math.max(table.getTotalSize(), containerWidth - 16);

    return (
        <div className="w-full border rounded-lg overflow-hidden relative">
            {/* Toast statusu na górze strony */}
            <div className="fixed inset-x-0 top-4 flex items-start justify-center pointer-events-none z-[70]">
                <div
                    className={`pointer-events-auto transform transition-all duration-300 ease-in-out ${
                        statusToastVisible ? "translate-y-0 opacity-100" : "-translate-y-6 opacity-0"
                    }`}
                >
                    {statusToast && (
                        <Toast
                            message={statusToast.message}
                            type={statusToast.type}
                            onClose={() => {
                                setStatusToastVisible(false);
                                setTimeout(() => setStatusToast(null), 300);
                            }}
                        />
                    )}
                </div>
            </div>
            {userMismatchAlert && (
                <AlertPopup
                    {...getAlertProps("userMismatch", {
                        rowStatus: userMismatchAlert.rowStatus,
                        rowUserName: userMismatchAlert.rowUserName,
                    })}
                    onClose={() => setUserMismatchAlert(null)}
                />
            )}
            {stock2ReservationAlert && (
                <AlertPopup
                    {...getAlertProps("stock2")}
                    onClose={() => setStock2ReservationAlert(false)}
                />
            )}
            {statusModal}
            {detailsRow && (
                <AutoDetailsModal
                    data={detailsRow}
                    onClose={() => setDetailsRow(null)}
                />
            )}
            <div className="p-3 bg-gray-50 border-b">
                <div className="grid grid-cols-[5fr_1fr]">
                    <div className="flex flex-wrap gap-3 items-center">
                        {Object.entries(preset.filters)
                            .filter(([, value]) => value)
                            .map(([key, rawValue]) => {
                                const value = String(rawValue);

                                const column = columns.find((col) => col.meta?.key === key);
                                if (!column) return null;

                                let displayValue: string = value ?? "";
                                if (column.meta?.type === "boolean") {
                                    displayValue = value === "1" ? "TAK" : "NIE";
                                }

                                if (column.meta?.type === "select") {
                                    const nullableLabel = nullableOptions.find((o) => o.value === value)?.label;
                                    if (nullableLabel) {
                                        displayValue = nullableLabel;
                                    } else if (value.startsWith("[")) {
                                        try {
                                            const parsed = JSON.parse(value);
                                            if (Array.isArray(parsed)) {
                                                const filteredValues = parsed
                                                    .map((item) => String(item ?? "").trim())
                                                    .filter(Boolean);

                                                if (filteredValues.length === 0) return null;
                                                displayValue = filteredValues.length === 1
                                                    ? filteredValues[0]
                                                    : `${filteredValues.length} elem.`;
                                            }
                                        } catch {
                                        }
                                    }
                                }

                                if (value.startsWith("{")) {
                                    try {
                                        const parsed = JSON.parse(value);

                                        if (column.meta?.type === "numberInput") {
                                            const isNullableOption = nullableOptions.some(o => o.value === parsed.option);
                                            if (!isNullableOption && !parsed.value) return null;
                                            displayValue = isNullableOption ? parsed.option : `${parsed.option} ${parsed.value}`;
                                        } else if (column.meta?.type === "date") {
                                            const from = parsed.from ? parsed.from.split("T")[0] : undefined;
                                            const to = parsed.to ? parsed.to.split("T")[0] : undefined;
                                            if (from && to) displayValue = `${from} - ${to}`;
                                            else if (from && !to) displayValue = `od ${from}`;
                                            else if (!from && to) displayValue = `do ${to}`
                                            else displayValue = "Brak daty";
                                        }
                                    } catch {

                                    }
                                }

                                return (
                                    <div
                                        key={key}
                                        className="flex items-center bg-gray-200 text-sm px-2 py-1 rounded-full"
                                    >
                                        <span
                                            className="font-medium">{column?.meta?.label}:</span>&nbsp;{displayValue}
                                        <X
                                            size={14}
                                            className="ml-1 cursor-pointer hover:text-red-500 transition"
                                            onClick={() =>
                                                setFilters({
                                                    ...preset.filters,
                                                    [key]: ""
                                                })
                                            }
                                        />
                                    </div>
                                );
                            })}
                    </div>
                    <div className="flex items-center justify-end space-x-4">
                        {process.env.NODE_ENV === 'development' && (
                            <select
                                value={currentDatabase}
                                onChange={(e) => handleDatabaseChange(e.target.value)}
                                disabled={isChangingDatabase}
                                className="px-3 py-1.5 text-sm border rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <option value="BEMO_COMM">BM</option>
                                <option value="ACLUB_COMM">AC</option>
                            </select>
                        )}
                        {Object.values(preset.filters).some((v) => v) && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setFilters({})}
                                className="ml-auto"
                            >
                                Resetuj wszystkie
                            </Button>
                        )}
                        <FilterPresetButton
                            tablePreset={tablePreset}
                        />
                        <ColumnVisibilityToggler
                            table={table}
                        ></ColumnVisibilityToggler>
                    </div>
                </div>
            </div>
            <div>
                <TooltipProvider>
                    <ReactTable
                        table={table}
                        items={flatRows}
                        virtualizerOptions={{
                            estimateSize,
                            measureElement: (el) => el.getBoundingClientRect().height,
                            overscan: 8,
                        }}
                        totalTableWidth={totalTableWidth}
                        state={tableState}
                        virtualizerRef={virtualizerRef}
                        outerContainerRef={(node) => {
                            outerContainerRef.current = node;
                        }}
                        tableContainerRef={(node) => {
                            tableContainerRef.current = node;
                        }}
                        renderItem={(item, virtualItem) => {
                            const row = item.row;
                            const status = row.original.f_supp_2_status_desc || "";
                            const statusNumber = status ? status.split("-")[0].trim() : "";

                            if (item.type === "row") {
                                const isHighlighted = highlightId === String(row.original.f_id);
                                const isForcedRow = forcedRow && String(row.original.f_id) === String(forcedRow.f_id);
                                return (
                                    <TableRow
                                        key={row.id}
                                        className={cn(
                                            rowItemStyles({
                                                color: getRowColor(statusNumber, row.original.f_supp_2_doc_invc_o_diff),
                                            }),
                                            isHighlighted && "bg-yellow-300 transition-colors duration-300 ease-in-out",
                                            isForcedRow && "ring-2 ring-amber-400/70"
                                        )}
                                        title={isForcedRow ? "Wiersz spoza aktywnych filtrów" : undefined}
                                        style={{
                                            height: `${virtualItem.size}px`,
                                            transform: `translateY(${virtualItem.start}px)`,
                                        }}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <ReactTableCell key={cell.id} cell={cell}/>
                                        ))}
                                    </TableRow>
                                );
                            }

                            return (
                                <AutoTableRowDetails
                                    key={`${row.id}-detail`}
                                    row={row}
                                    data={expandedData[row.id] ?? null}
                                    loading={false}
                                    virtualItem={virtualItem}
                                    containerWidth={containerWidth}
                                    outerContainerRef={outerContainerRef}
                                    onDetailsLoaded={() => virtualizerRef.current?.measure()}
                                />
                            );
                        }}
                    />

                    {activeFilterBox && (
                        <FilterToolbox
                            key={activeFilterBox.columnId}
                            columnId={activeFilterBox.columnId}
                            filters={preset.filters}
                            position={{x: activeFilterBox.x, y: activeFilterBox.y}}
                            onChange={(value) =>
                                setFilters({
                                    ...preset.filters,
                                    [activeFilterBox.columnId]: value
                                })
                            }
                            onClose={() => setActiveFilterBox(null)}
                        />
                    )}

                    {statusActionDropdown && (
                        <div
                            className="fixed z-[60] bg-white border rounded-lg shadow-lg p-2 min-w-[200px]"
                            style={{
                                left: `${statusActionDropdown.x}px`,
                                top: `${statusActionDropdown.y}px`,
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="text-xs font-medium mb-2 px-2 text-gray-500">Wybierz akcję:</div>
                            {statusActionDropdown.actions.map((actionCode) => {
                                const actionLabel = changeStatusActionLabels[actionCode] || `Akcja ${actionCode}`;
                                const row = table.getRowModel().rows.find(r => r.id === statusActionDropdown!.rowId);
                                if (!row) return null;

                                return (
                                    <Button
                                        variant="ghost"
                                        type="button"
                                        key={actionCode}
                                        className="w-full justify-start text-left px-3 py-2 text-sm hover:bg-gray-100 rounded transition-colors"
                                        onClick={async () => {
                                            setStatusActionDropdown(null);
                                            await openStatusModal(row, actionCode);
                                        }}
                                    >
                                        {actionLabel}
                                    </Button>
                                );
                            })}
                            <Button
                                variant="ghost"
                                type="button"
                                className="w-full justify-start text-left px-3 py-2 text-sm hover:bg-gray-100 rounded transition-colors text-gray-500 mt-1 border-t"
                                onClick={() => setStatusActionDropdown(null)}
                            >
                                Anuluj
                            </Button>
                        </div>
                    )}
                </TooltipProvider>
            </div>
            <div
                className="flex items-center justify-between p-2 text-sm text-muted-foreground bg-neutral-100 rounded"
            >
                <span> Znaleziono {table.getFilteredRowModel().rows.length} wierszy </span>
                <span className="flex items-center gap-2">
                    <span>Limit l. rekordów:</span>
                    <Input
                        value={rowLimit ?? ""}
                        onChange={(e) => {
                            const val = e.target.value;
                            setRowLimit(val === "" ? undefined : Number(val));
                        }}
                        placeholder="Limit.."
                        className="w-24 bg-white"
                    />
                </span>
            </div>
        </div>
    );
}
